import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, AlertTriangle } from 'lucide-react';
export function ProblemSection() {
  return <section className="bg-cream py-20 px-4 md:px-8" data-id="element-133">
      <div className="max-w-4xl mx-auto" data-id="element-134">
        {/* Opening Hook */}
        <motion.div className="prose prose-lg md:prose-xl max-w-none text-charcoal mb-16" initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-135">
          <p className="font-medium text-xl md:text-2xl leading-relaxed mb-6" data-id="element-136">
            You open the envelope from the County and feel a pit in your
            stomach. Your house payment went up again.
          </p>
          <p className="leading-relaxed mb-6" data-id="element-137">
            The bank says it's an "Escrow Shortage," but the reality is more
            bitter: the government decided, without ever stepping foot in your
            home, that you are wealthier than you really are—and now they want a
            bigger slice of your paycheck.
          </p>
          <p className="leading-relaxed" data-id="element-138">
            It's not your imagination. The system is designed for you to assume
            the "Tax Assessor's" bill is divine law. But it isn't. It's a
            billing proposal based on cold algorithms that don't know about your
            leaky roof, your 20-year-old kitchen, or the cracks in your
            driveway.
          </p>
        </motion.div>

        <motion.hr className="border-gold/30 border-t-2 w-32 mx-auto mb-16" initial={{
        scaleX: 0
      }} whileInView={{
        scaleX: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-139" />

        {/* The Problem Callout */}
        <motion.div className="bg-navy border-l-4 border-gold rounded-r-lg p-8 md:p-10 shadow-xl mb-16 text-white relative overflow-hidden" initial={{
        opacity: 0,
        x: -30
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-140">
          <AlertTriangle className="absolute -right-10 -top-10 w-48 h-48 text-white opacity-5 pointer-events-none" data-id="element-141" />

          <h3 className="text-2xl md:text-3xl font-bold text-gold mb-6 flex items-center gap-3" data-id="element-142">
            The "Phantom Tax" Trap
          </h3>
          <p className="text-lg md:text-xl leading-relaxed mb-6 text-gray-200" data-id="element-143">
            Every year, thousands of homeowners across the US watch their
            monthly mortgage skyrocket by{' '}
            <strong className="text-white" data-id="element-144">$100, $200, or even $400</strong> due
            to unfair automatic appraisals. The County assumes your modest home
            is worth as much as the luxury remodel three doors down just because
            you share a zip code.
          </p>
          <p className="text-lg md:text-xl leading-relaxed text-gray-200" data-id="element-145">
            You end up paying a "Phantom Tax" for luxuries you don't have. If
            you don't complain, the County assumes you agree. Meanwhile, your
            monthly check gets smaller, and your peace of mind disappears.
          </p>
        </motion.div>

        {/* Why What You Tried Didn't Work */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-146">
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-10" data-id="element-147">
            Why What You Tried Didn't Work
          </h3>

          <div className="space-y-6" data-id="element-148">
            {[{
            title: 'Calling the bank is useless',
            desc: 'because they only collect what the County orders them to charge you.'
          }, {
            title: "Complaining over the phone to the Assessor's office yields no results",
            desc: 'because there is no formal legal record of your claim.'
          }, {
            title: "Waiting for home prices to drop doesn't automatically lower your taxes",
            desc: 'the government rarely reduces an assessment on its own initiative.'
          }, {
            title: 'Hiring a tax attorney usually costs 50% of whatever savings you achieve',
            desc: 'leaving you right back where you started.'
          }].map((item, i) => <motion.div key={i} className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-sm border border-gray-100" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.15,
            duration: 0.5
          }} data-id="element-149">
                <XCircle className="w-8 h-8 text-red flex-shrink-0 mt-1" data-id="element-150" />
                <div data-id="element-151">
                  <h4 className="text-xl font-bold text-charcoal mb-1" data-id="element-152">
                    {item.title}
                  </h4>
                  <p className="text-lg text-gray-600" data-id="element-153">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}